import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { BsGrid3X3GapFill } from 'react-icons/bs';
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from 'react-icons/fa6';
import { FaFileAlt, FaLocationArrow } from 'react-icons/fa';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import UploadImage from '../components/UploadImage';
import { MdDelete } from 'react-icons/md';
import { getMediaByFolder, deleteMediaFromFolder } from '../Api';
import { toast } from 'react-toastify';
import { BiSolidCoupon } from 'react-icons/bi';

const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  height: 650,
  bgcolor: 'background.paper',
  border: '1px solid transparent',
  borderRadius: 1,
  shadow: 24,
  p: 4,
};

function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function DshContent({ onLogout }) {
  const navigate = useNavigate();
  const router = useDemoRouter('/dashboard-content');
  const [loading, setLoading] = useState(false);
  const [contentImages, setContentImages] = useState([]);
  const [contentPage, setContentPage] = useState('slider');
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    fetchMedia(contentPage);
  };

  const fetchMedia = async (folderName) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMediaByFolder(folderName, 1, 10);
      setContentImages(response.data || []);
    } catch (error) {
      console.error('Fetch media error:', error);
      setError(error.message || 'Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia(contentPage);
  }, [contentPage]);

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const deleteMedia = async (id) => {
    if (window.confirm("Are you sure want to delete this image?")) {
      setLoading(true)
      try {
        setError(null);
        await deleteMediaFromFolder(id, contentPage);
        fetchMedia(contentPage);
        toast.success("Image deleted successfully")
      } catch (error) {
        console.error('Delete media error:', {
          id,
          folderName: contentPage,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setError(error.message || 'Failed to delete media');
      } finally {
        setLoading(false)
      }
    }
  };

  const onContentChange = (slug) => {
    setContentPage(slug);
  };

  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: '#0165AB', color: '#fff' }}
    >
      Logout
    </Button>
  );

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme}
      branding={branding}
    >
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
      >
        <PageContainer>
          {loading ? (
            <Grid container spacing={1}>
              <Grid size={5} />
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={4}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={8}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={150} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={3}>
                <Skeleton height={100} />
              </Grid>
            </Grid>
          ) : (
            <section className="content-section">
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <div className="content-nav">
                <ul className="d-flex">
                  <li
                    className={contentPage === 'slider' ? 'active' : ''}
                    onClick={() => onContentChange('slider')}
                  >
                    Slider
                  </li>
                  <li
                    className={contentPage === 'banner' ? 'active' : ''}
                    onClick={() => onContentChange('banner')}
                  >
                    Banner
                  </li>
                  <li
                    className={contentPage === 'offer' ? 'active' : ''}
                    onClick={() => onContentChange('offer')}
                  >
                    Offer
                  </li>
                </ul>
              </div>
              <div className="content-box">
                {contentPage === 'slider' && (
                  <section className="slider-content">
                    <div>
                      <button onClick={handleOpen}>Add Slider Image</button>
                    </div>
                  </section>
                )}
                {contentPage === 'banner' && (
                  <section className="banner-content">
                    <div>
                      <button onClick={handleOpen}>Add Banner Image</button>
                    </div>
                  </section>
                )}
                {contentPage === 'offer' && (
                  <section className="offer-content">
                    <div>
                      <button onClick={handleOpen}>Add Offer Image</button>
                    </div>
                  </section>
                )}
              </div>
              <div>
                {contentImages.length > 0 ? (
                  contentImages.map((item) => (
                    <div style={{ position: 'relative' }} key={item.id}>
                      <img
                        style={{ height: '100%', width: '100%', margin: '10px 0 0' }}
                        src={item.url}
                        alt={item.title || 'Content image'}
                      />
                      <button
                        style={{
                          position: 'absolute',
                          top: '8%',
                          right: '1%',
                          height: '40px',
                          width: '40px',
                          border: '1px solid transparent',
                          background: 'red',
                          borderRadius: '5px',
                          cursor: 'pointer',
                        }}
                        onClick={() => deleteMedia(item.id)}
                      >
                        <MdDelete style={{ color: '#fff', fontSize: '20px' }} />
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No images available!</p>
                )}
              </div>
            </section>
          )}
        </PageContainer>
      </DashboardLayout>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Upload {contentPage.charAt(0).toUpperCase() + contentPage.slice(1)} Image
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <UploadImage handleClose={handleClose} contentPage={contentPage} />
          </Typography>
        </Box>
      </Modal>

      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
}

export default DshContent;